import {useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  usePlayAccent,
  usePlayContentPadding,
  usePlayFooterStyle,
  usePlayPrimaryAction,
  usePlayRuleSnap,
  usePlayRuling,
  usePlayStackGap,
  usePlayTextStyle,
} from '#/components/flashbooks/FlashBookPlayTheme'

const ACCENT = SLIDE_ACCENT.accordion

export function AccordionSlidePlay({
  slide,
  onAnswer,
  primaryColor,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(24)
  // The action strip is page furniture: on ruled paper it is the button alone,
  // printed on the lines, a whole number of rules tall.
  const footerStyle = usePlayFooterStyle()
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // A whole number of the page's rules between stacked sections.
  const stackGap = usePlayStackGap(16)
  // A section's heading and its reading are set in the book's own type, and its
  // leading is a whole number of lines: the rows are only lightly tinted, so the
  // page's lines show through them and the writing has to be on them.
  const titleText = usePlayTextStyle('label')
  const bodyText = usePlayTextStyle('body')
  const snap = usePlayRuleSnap()
  const step = usePlayRuling()
  const headerPad = {paddingVertical: snap(16)}
  /**
   * A ruled page has no room for a hairline: a 1px frame is one pixel per side
   * that the ruling does not have, and it puts the row's edges — and every row
   * under it — off the lines. The fill still reads as a pasted-on panel.
   */
  const frameOnRuled = step > 0 ? ({borderWidth: 0} as const) : null
  /**
   * On ruled paper the list is a column of ruled rows: a whole rule between two
   * items, and no stray margin above the first, so every row starts on a line.
   */
  const listOnRuled = step > 0 ? {gap: stackGap, marginTop: 0} : undefined
  /**
   * The revealed body is written *under* its heading line, separated by one
   * blank rule rather than by 12px and a hairline the ruling does not have.
   */
  const bodyOnRuled =
    step > 0
      ? {paddingTop: step, paddingBottom: 0, borderTopWidth: 0}
      : undefined
  // Inside a book the open state wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  const [openIds, setOpenIds] = useState<Set<string>>(new Set())

  const toggleItem = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleContinue = () => {
    onAnswer({
      isCorrect: true,
      points: 0,
      maxPoints: 0,
      userAnswer: Array.from(openIds),
    })
  }

  const items = slide.accordionItems ?? []

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={[ss.scrollContent, {padding, gap: stackGap}]}>
        {!!slide.contentTitle && (
          <QuestionHeading>{slide.contentTitle}</QuestionHeading>
        )}

        <View style={[ss.list, listOnRuled]}>
          {items.map(item => {
            const isOpen = openIds.has(item.id)
            return (
              <View
                key={item.id}
                style={[
                  ss.item,
                  {
                    borderColor: palette.border,
                    backgroundColor: palette.surface,
                  },
                  frameOnRuled,
                  isOpen && {borderColor: accent + '66'},
                ]}>
                <TouchableOpacity
                  style={[ss.itemHeader, headerPad]}
                  onPress={() => toggleItem(item.id)}
                  accessibilityRole="button"
                  accessibilityLabel={item.title}
                  accessibilityHint={undefined}>
                  <Text
                    style={[ss.itemTitle, titleText, {color: palette.body}]}>
                    {item.title}
                  </Text>
                  <Feather
                    name={isOpen ? 'chevron-up' : 'chevron-down'}
                    size={18}
                    color={accent}
                  />
                </TouchableOpacity>
                {isOpen && (
                  <View
                    style={[
                      ss.itemBody,
                      {borderTopColor: palette.border},
                      bodyOnRuled,
                    ]}>
                    <Text
                      style={[
                        ss.itemBodyText,
                        bodyText,
                        {color: palette.body},
                      ]}>
                      {item.body}
                    </Text>
                  </View>
                )}
              </View>
            )
          })}
        </View>
      </PlayScroller>

      <View style={[ss.footer, {borderTopColor: palette.border}, footerStyle]}>
        {/* A reveal block has nothing to grade, so it records itself as read —
            the same action reading blocks offer. It deliberately does *not*
            promise to move on: the next question is already below, and the
            page turns from the page footer. */}
        <TouchableOpacity
          style={[
            ss.continueBtn,
            {backgroundColor: primaryColor},
            action.style,
          ]}
          onPress={handleContinue}
          accessibilityRole="button"
          accessibilityLabel={l`Mark as read`}
          accessibilityHint={undefined}>
          <Text
            style={[
              ss.continueBtnText,
              {color: action.ink(true) ?? palette.onAccent},
              action.labelStyle(true),
            ]}>
            <Trans>Mark as read</Trans>
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  scrollContent: {padding: 24, gap: 16},
  header: {
    fontSize: 22,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    marginTop: 8,
    lineHeight: 30,
  },
  list: {gap: 10, marginTop: 4},
  item: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
    backgroundColor: PLAY_SURFACE.surface,
    overflow: 'hidden',
  },

  itemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  itemTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: PLAY_SURFACE.text,
    marginRight: 8,
  },
  itemBody: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
    paddingTop: 12,
  },
  itemBodyText: {
    fontSize: 14,
    color: PLAY_SURFACE.textFaint,
    lineHeight: 22,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
  },
  continueBtn: {borderRadius: 14, paddingVertical: 15, alignItems: 'center'},
  continueBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
})
